import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useCallback, useEffect, useMemo } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { useQuery } from '@tanstack/react-query';
import format from 'date-fns/format';
import getDay from 'date-fns/getDay';
import ko from 'date-fns/locale/ko';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import dayjs from 'dayjs';
import { Calendar as BigCalendar, View, Views, dateFnsLocalizer } from 'react-big-calendar';
import { createGlobalStyle } from 'styled-components';

import apiClient from '~/config/apiClient';

import { useProjectContext } from '../projects/context/ProjectProvider';

import CalendarDayView from './Calendar.DayView';
import { CalendarDetail } from './Calendar.Detail';
import { CalendarEvent } from './Calendar.Event';
import { CalendarToolbar } from './Calendar.Toolbar';
import { useCalendarDetailStore, useCalendarViewTypeStore, useSelectedDateStore } from './store';
import { CalendarDataType } from './types';

const locales = {
  ko: ko,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const GlobalStyles = createGlobalStyle`
 .rbc-time-content{
    display:none;
 }

 .rbc-time-header-gutter  {
    display:none;
 }

 .rbc-row-content{
    z-index:auto;
 }

 .rbc-off-range{
    color:#bfbfbf;
 }

 .rbc-off-range-bg{
    background:#f5f5f5;
 }
 

 .rbc-event,.rbc-event-content {
    background:none !important;
    font-weight:400;
    font-size:10px;
    margin-bottom:2px;
    height:15px;
    border-radius:2px;
    border:none;
    padding:0 !important;
    display:flex;
    align-items:center;
    width:100%;
    line-height:15px;
 }


 .rbc-show-more{
    color:#7F8892;
    font-size:10px;
    font-weight:400;
 }

 .rbc-header:first-child {
    color:red;
 }
`;

export const Calendar = () => {
  const { project } = useProjectContext();
  const reset = useCalendarDetailStore((state) => state.reset);
  const { type, setType } = useCalendarViewTypeStore();
  const { date, setDate } = useSelectedDateStore();

  const fetchData = async () => {
    const params = {
      project_id: project?._id,
      date: date.toISOString(),
    };
    try {
      const { data } = await apiClient.get<CalendarDataType[]>('/schedules/calendar', {
        params,
      });

      return data.map((item) => {
        return {
          ...item,
          allDay: true,
        };
      });
    } catch (e) {
      console.log(e);
    }
  };

  const { data: events } = useQuery({
    queryKey: ['getSchedules', project?._id, date],
    queryFn: fetchData,
  });

  const { components, defaultDate, views, formats } = useMemo(
    () => ({
      components: {
        toolbar: CalendarToolbar,
        day: {
          event: CalendarEvent,
        },
        month: {
          event: CalendarEvent,
        },
      },
      defaultDate: new Date(),
      views: {
        day: CalendarDayView,
        month: true,
      },
      formats: {
        monthHeaderFormat: (date: any, culture: any): string => {
          if (culture === 'ko' || culture === 'ko-KR') return dayjs(date).format('YYYY년 MMM');
          return dayjs(date).format('YYYY MMM');
        },
        dayHeaderFormat: (date: any) => {
          return dayjs(date).format('ll');
        },
        weekdayFormat: (date: any) => {
          return dayjs(date).format('dd');
        },
      },
    }),
    [],
  );

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const onNavigate = useCallback((newDate: Date) => setDate(newDate), [setDate]);
  const onView = useCallback((newView: View) => setType(newView), [setType]);
  const onDrillDown = useCallback(
    (newDate: Date, view: View) => {
      setDate(newDate);
      setType(view);
    },
    [setDate, setType],
  );

  return (
    <>
      <Box height={'100vh'} position={'relative'}>
        <GlobalStyles />
        <Box height={'100%'} background={'#FFFFFF'}>
          <BigCalendar
            date={date}
            view={type}
            localizer={localizer}
            culture={'ko'}
            defaultDate={defaultDate}
            events={events}
            components={components}
            defaultView={Views.MONTH}
            views={views}
            startAccessor={'startDate'}
            endAccessor={'endDate'}
            formats={formats}
            onNavigate={onNavigate}
            onDrillDown={onDrillDown}
            onView={onView}
          />
        </Box>
        <CalendarDetail />
      </Box>
    </>
  );
};
